import { useQuery } from "@tanstack/react-query";
import { resourceEngagementsKeys } from "@/lib/query";

/**
 * Response contract for `GET /api/dashboard/resources/:id/engagements`.
 * Mirrors the backend contract exactly — do not rename fields locally.
 *
 * `points` is capacity (points-based); `hoursLogged` is utilization (hours-based).
 * These are never the same unit and must never be combined, summed, or compared.
 */
export type EngagementRole = "R1" | "R2" | "R3" | "R4" | "Supporting";

export type EngagementStandardTier = "Support" | "Growth" | "Standard" | "Strategic";

export type EngagementConstraintGroup = "Domain" | "Platform" | "Offering" | "ICP";

export interface EngagementPodMember {
  resourceId: string;
  name: string;
  role: string;
}

export interface EngagementConstraint {
  group: EngagementConstraintGroup;
  name: string;
}

export interface EngagementEntry {
  assignmentId: string;
  solutionId: string;
  solutionName: string;
  accountName: string;
  engagementStandardTier: EngagementStandardTier;
  role: EngagementRole;
  /** What the seat is for on that Solution; absent when none is set. */
  seatDiscipline?: { code: string; name: string };
  /** Capacity — points-based. Never combine with hoursLogged. */
  points: number;
  /** ISO timestamp. */
  effectiveStart: string;
  /** ISO timestamp, or null when the Assignment is still active. */
  effectiveEnd: string | null;
  /** Co-Primaries on the same Pod. Empty array means unavailable/none — not "Solo". */
  podComposition: EngagementPodMember[];
  /** Utilization — hours-based. Never combine with points. */
  hoursLogged: number;
  constraints: EngagementConstraint[];
}

export interface ResourceEngagements {
  solutionsStaffedCount: number;
  totalHoursLogged: number;
  engagements: EngagementEntry[];
}

async function fetchResourceEngagements(resourceId: string): Promise<ResourceEngagements> {
  const res = await fetch(`/api/dashboard/resources/${resourceId}/engagements`);
  if (!res.ok) throw new Error("engagements unavailable");
  return res.json();
}

/**
 * Read-only Engagement (Assignment) history for a Resource, sourced from the
 * dashboard engagements endpoint. Scoped per-resource and gated on `enabled` so
 * callers can defer the fetch until the Resource Detail drawer is actually open
 * (per the "fetch fresh data when opened" dialog rule in .claude/rules/frontend.md).
 */
export function useResourceEngagements(resourceId: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && resourceId.length > 0;
  return useQuery<ResourceEngagements>({
    queryKey: resourceEngagementsKeys.detail(resourceId),
    queryFn: () => fetchResourceEngagements(resourceId),
    enabled,
  });
}
